import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';
import { AtualizacaoDisponivelService } from '@core/pwa/atualizacao-disponivel.service';
import { recarregarComVersaoNova } from '@core/pwa/recarregar-app';

/** De quanto em quanto tempo uma aba aberta pergunta ao servidor se saiu versão nova. */
const INTERVALO_DE_CHECAGEM_MS = 15 * 60 * 1000;

/** Piso entre duas checagens: alternar de aba não pode virar uma rajada de requisições. */
const INTERVALO_MINIMO_MS = 60 * 1000;

/**
 * Avisa quando há uma versão nova publicada e recarrega a aplicação a pedido do usuário.
 *
 * <p>É o contrapeso do cache do service worker: com o app shell em cache, quem já abriu o sistema
 * continua rodando o build antigo mesmo depois de um deploy — e continuaria até fechar todas as
 * abas. Numa aplicação que compartilha banco com o sistema legado, isso significa uma tela velha
 * conversando com uma API nova. O aviso deixa a decisão com quem está usando: recarregar no meio
 * de um pedido preenchido custaria o formulário.</p>
 *
 * <p>O service worker só procura atualização quando a aplicação carrega. Sem as checagens deste
 * serviço, a aba que ficou aberta desde a manhã atravessa quantos deploys houver sem descobrir
 * nenhum — e é justamente essa aba que o aviso precisa alcançar. Por isso a pergunta se repete no
 * relógio e, sobretudo, quando o usuário volta para a aba: é aí que ele está prestes a agir.</p>
 *
 * <p>Quem mostra o aviso é o {@link AtualizacaoDisponivelService}, o mesmo que atende o anúncio do
 * backend: duas origens para a mesma oferta, uma interface só. Aqui fica o que é do worker —
 * baixar o build novo e o estado irrecuperável, que ele é o único a conhecer.</p>
 *
 * <p>Nada acontece em desenvolvimento nem em navegador sem suporte: {@link SwUpdate#isEnabled} é
 * falso, e os eventos nunca chegam.</p>
 */
@Injectable({ providedIn: 'root' })
export class AtualizacaoAppService {

  private readonly updates = inject(SwUpdate);
  private readonly snackbar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly disponivel = inject(AtualizacaoDisponivelService);

  /** Instante da última checagem, para o piso entre elas. */
  private ultimaChecagem = 0;

  vigiar(): void {
    if (!this.updates.isEnabled) {
      return;
    }

    this.updates.versionUpdates
      .pipe(
        filter((evento): evento is VersionReadyEvent => evento.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.disponivel.anunciarBuildNovo());

    /**
     * Cache irrecuperável (arquivo do build antigo que não existe mais no servidor): o service
     * worker não tem como servir a aplicação, e só um recarregamento limpo resolve. Aqui não há o
     * que perguntar — a alternativa é uma tela quebrada.
     */
    this.updates.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.snackbar.open(
          'A aplicação precisa ser recarregada para continuar.', 'RECARREGAR',
          { duration: 0 })
          .onAction()
          .subscribe(() => this.recarregar());
      });

    this.agendarChecagens();
  }

  /**
   * Pergunta ao servidor se há versão nova, sem incomodar quem estiver offline.
   *
   * <p>Uma falha aqui é rotina — rede caída, servidor momentaneamente fora — e não tem o que
   * comunicar: a próxima checagem tenta de novo.</p>
   */
  async checarAgora(): Promise<void> {
    if (!this.updates.isEnabled) {
      return;
    }
    this.ultimaChecagem = Date.now();
    try {
      await this.updates.checkForUpdate();
    } catch {
      // silêncio proposital: ver o comentário acima
    }
  }

  /** Checagem no relógio e no retorno à aba, ambas desligadas quando a aplicação morre. */
  private agendarChecagens(): void {
    const relogio = setInterval(() => void this.checarAgora(), INTERVALO_DE_CHECAGEM_MS);

    // Voltar para a aba é o melhor momento para perguntar: o usuário está prestes a usar o
    // sistema, e é a única deixa confiável numa aba que passou horas em segundo plano.
    const aoVoltarParaAba = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (Date.now() - this.ultimaChecagem < INTERVALO_MINIMO_MS) {
        return;
      }
      void this.checarAgora();
    };
    document.addEventListener('visibilitychange', aoVoltarParaAba);

    this.destroyRef.onDestroy(() => {
      clearInterval(relogio);
      document.removeEventListener('visibilitychange', aoVoltarParaAba);
    });
  }

  /**
   * Estado irrecuperável é justamente o caso em que o cache do worker está inservível: o reload
   * precisa passar por cima dele — ver {@link recarregarComVersaoNova}.
   */
  private recarregar(): void {
    void recarregarComVersaoNova(this.updates);
  }
}
